<template>
  <Modal :name="$options.name"
         @close="resetModal"
         :is-loading="isLoading"
         title="Bizottsági válasz módosítás">
    <template #body
              v-if="question">
      <h5 class="title is-5 mb-2 p-1 has-background-primary-light">Kérdés</h5>
      <b>{{ question.number }}. </b>
      <br>
      <div v-html="question.text"></div>
      <h5 class="title is-5 my-2 p-1 has-background-primary-light">Eredeti válasz</h5>
      <div v-if="question.answerOptions.length > 0"
           class="q-answer-options is-flex is-justify-content-center">
        <b-field>
          <AnswerCheckbox v-if="question.answerOptions.includes(1)"
                          :editable="false"
                          :label="getAnswerText(1)"
                          :is-selected="question.originalAnswer === 1"/>
          <AnswerCheckbox v-if="question.answerOptions.includes(0)"
                          :editable="false"
                          :label="getAnswerText(0)"
                          :is-selected="question.originalAnswer === 0"/>
          <AnswerCheckbox v-if="question.answerOptions.includes(2)"
                          :editable="false"
                          :label="getAnswerText(2)"
                          :is-selected="question.originalAnswer === 2"/>
        </b-field>
      </div>
      <div v-if="originalVisibleSubQuestions.length > 0"
           class="q-subquestions">
        <SubQuestion v-for="visible in originalVisibleSubQuestions"
                     :key="`originalSubQuestion_${visible.subQuestion.id}`"
                     :sub-question="visible.subQuestion"
                     :depth="visible.depth"
                     :editable="false"/>
      </div>
      <h5 class="title is-5 my-2 p-1 has-background-primary-light">Új válasz</h5>
      <div v-if="question.answerOptions.length > 0"
           class="q-answer-options is-flex is-justify-content-center">
        <b-field>
          <AnswerCheckbox v-if="question.answerOptions.includes(1)"
                          @click="answerVal = 1"
                          :label="getAnswerText(1)"
                          :is-selected="answerVal === 1"/>
          <AnswerCheckbox v-if="question.answerOptions.includes(0)"
                          @click="answerVal = 0"
                          :label="getAnswerText(0)"
                          :is-selected="answerVal === 0"/>
          <AnswerCheckbox v-if="question.answerOptions.includes(2)"
                          @click="answerVal = 2"
                          :label="getAnswerText(2)"
                          :is-selected="answerVal === 2"/>
        </b-field>
      </div>
      <div v-if="newVisibleSubQuestions.length > 0"
           class="q-subquestions">
        <SubQuestion v-for="visible in newVisibleSubQuestions"
                     :key="`newSubQuestion_${visible.subQuestion.id}`"
                     :sub-question="visible.subQuestion"
                     :depth="visible.depth"
                     :editable="true"
                     @change="setSubQuestionAnswer(visible.subQuestion.id, $event)"/>
      </div>
      <h5 class="title is-5 my-2 p-1 has-background-primary-light">Bizottsági megjegyzés</h5>
      <b-field label="">
        <b-input v-model="committeeComment"
                 type="textarea"></b-input>
      </b-field>
    </template>
    <template #footer>
      <b-button @click="modifyAnswer"
                type="is-primary">Módosítás
      </b-button>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Modals/common/BasicModal'
import AnswerCheckbox from '@/components/Survey/AnswerCheckbox'
import SubQuestion from '@/components/Survey/SubQuestion'
import { getAnswerText } from '@/utils/utils'
import { getSubQuestionAnswerPayload, getVisibleSubQuestions, isSubQuestionAnswered } from '@/utils/subQuestions'

// A nem módosított al-kérdésnél az original_answer nem feltétlenül kitöltött,
// ilyenkor az aktuális válasz az eredeti.
const originalSubQuestionAnswerOf = subQuestion => subQuestion.is_answer_modified
  ? subQuestion.original_answer
  : subQuestion.answer

// A SubQuestion a subQuestion.answer-t jeleníti meg, ezért a megjelenítendő
// választ beletesszük a másolatba.
const withAnswer = (visibleSubQuestions, answerOf) => visibleSubQuestions.map(({ subQuestion, depth }) => ({
  subQuestion: { ...subQuestion, answer: answerOf(subQuestion) },
  depth,
}))

export default {
  name: 'SurveyModifyQuestionOriginalAnswer',
  components: {
    AnswerCheckbox,
    Modal,
    SubQuestion,
  },
  data () {
    return {
      isLoading: false,
      answerVal: null,
      committeeComment: null,
      // al-kérdés id → az új válasz (select: opció-id, checkbox: opció-id-k tömbje)
      subQuestionAnswers: {},
    }
  },
  methods: {
    getAnswerText,
    resetModal () {
      this.isLoading = false
      this.answerVal = null
      this.committeeComment = null
      this.subQuestionAnswers = {}
    },
    closeModal () {
      this.$store.dispatch('closeModal', this.$options.name)
    },
    subQuestionAnswerOf (subQuestion) {
      return this.subQuestionAnswers[subQuestion.id]
    },
    setSubQuestionAnswer (subQuestionID, answer) {
      this.subQuestionAnswers = { ...this.subQuestionAnswers, [subQuestionID]: answer }
    },
    async modifyAnswer () {
      this.isLoading = true
      console.log(this.answerVal, this.committeeComment)
      if (this.answerVal === null || ![0, 1, 2].includes(Number(this.answerVal)) || !this.committeeComment) {
        this.$store.dispatch('showMessage', {
          type: 'warning',
          msg: 'Új válasz és a Bizottsági megjegyzés kitöltése kötelező',
        })
      } else if (!this.isNewSubQuestionsAnswered) {
        this.$store.dispatch('showMessage', {
          type: 'warning',
          msg: 'Az új válaszhoz tartozó al-kérdések kitöltése kötelező',
        })
      } else {
        const payload = {
          surveyID: this.surveyID,
          questionID: this.question.id,
          answer: this.answerVal,
          committeeComment: this.committeeComment,
        }
        // Az al-kérdés mezőket az új válasz szerinti teljes állapottal küldjük:
        // ha az új válaszhoz nem tartozik al-kérdés, mindkettő null, így a régi törlődik.
        if (this.subQuestions.length > 0) {
          Object.assign(payload,
            getSubQuestionAnswerPayload(this.subQuestions, this.answerVal, this.subQuestionAnswerOf))
        }
        const response = await this.$store.dispatch('modifyAnswer', payload)
        if (response) {
          // A store-on keresztüli zárás nem váltja ki a Modal @close eseményét,
          // ezért az űrlapot itt kell alaphelyzetbe állítani.
          this.closeModal()
          this.resetModal()
        }
      }
      this.isLoading = false
    },
  },
  computed: {
    inspectionID () {
      return this.$store.state.modal.modalPayloadData[this.$options.name]?.inspectionID
    },
    surveyID () {
      return this.$store.state.modal.modalPayloadData[this.$options.name]?.surveyID
    },
    question () {
      return this.$store.state.modal.modalPayloadData[this.$options.name]?.question
    },
    subQuestions () {
      return this.question?.subQuestions || []
    },
    originalVisibleSubQuestions () {
      if (!this.question) {
        return []
      }
      return withAnswer(
        getVisibleSubQuestions(this.subQuestions, this.question.originalAnswer, originalSubQuestionAnswerOf),
        originalSubQuestionAnswerOf)
    },
    newVisibleSubQuestions () {
      if (this.answerVal === null) {
        return []
      }
      return withAnswer(
        getVisibleSubQuestions(this.subQuestions, this.answerVal, this.subQuestionAnswerOf),
        this.subQuestionAnswerOf)
    },
    isNewSubQuestionsAnswered () {
      return this.newVisibleSubQuestions.every(({ subQuestion }) => isSubQuestionAnswered(subQuestion, subQuestion.answer))
    },
  },
  watch: {
    question (newValue) {
      if (newValue) {
        if (newValue.isAnswerModified) this.answerVal = newValue.answer
        this.committeeComment = newValue.committeeComment
        // Az al-kérdések a jelenlegi válaszukkal indulnak; ha a bizottság más
        // főválaszt jelöl, az ahhoz tartozó al-kérdéseket kell kitöltenie.
        this.subQuestionAnswers = (newValue.subQuestions || []).reduce((answers, subQuestion) => ({
          ...answers,
          [subQuestion.id]: subQuestion.answer,
        }), {})
      }
    },
  },
}
</script>

<style scoped>
.q-subquestions {
  padding: 0 1rem .5rem;
}
</style>
